import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import type { FeatureLike } from 'ol/Feature';
import type { ProduceIconSource } from '@/components/produceIcon/produceArt';

/**
 * How the map draws itself: the İl outline, a listing pin, and a cluster.
 *
 * Kept out of the component because symbology is a design decision that gets
 * revised on its own, and because a style function has no business re-rendering
 * with React state.
 */

// orientation only — it marks the filtered region, it is not a clickable layer
export const borderStyle = new Style({
  stroke: new Stroke({ color: '#12664b', width: 2, lineDash: [6, 4] }),
});

/**
 * A listing pin — a teardrop in the brand's
 * deep green around a white disc, with the product drawn inside.
 *
 * Every listing gets this shape, art or not: a product whose
 * art has not arrived yet shows its emoji in the same disc, so the map never
 * mixes two kinds of marker while the set fills in. The art is drawn exactly
 * where the designers placed it inside their disc (produceArt.ts).
 *
 * The white disc is also what keeps a coloured emoji or drawing off the tiles:
 * it has no colour of its own to clash with — the old green-filled circle hid
 * 🫒🥬🌱.
 */

/** The designers' pin, in their own 24 × 28.681 units. */
const PIN_UNITS = { width: 24, height: 28.681 };
/** Their path, as exported — placed by the offset their SVG applied to it. */
const PIN_PATH_D =
  'M-248.585-5.463l.161.04a12.048,12.048,0,0,1,6.978,4.756l.068.1a12.011,12.011,0,0,1,1.936,8.856,14.226,14.226,0,0,1-4.464,7.626l-.225.225-.6.6-.378.378-1.319,1.32-1.228,1.229-1.057,1.058-.63.631-.591.591-.216.217-.3.3-.086.088a.925.925,0,0,1-.721.343l-.139,0a1.676,1.676,0,0,1-.826-.579l-.1-.1-.33-.33-.237-.237-.643-.643-.674-.674-1.131-1.132-1.306-1.307-1.393-1.394-.4-.4-.63-.631-.231-.231a11.988,11.988,0,0,1-1.058-1.122l-.086-.1a11.881,11.881,0,0,1-2.814-8.152l.007-.132a11.972,11.972,0,0,1,2.168-6.394l.13-.178a12.8,12.8,0,0,1,1.041-1.216l.094-.1a11.911,11.911,0,0,1,5.874-3.3l.139-.036A11.317,11.317,0,0,1-248.585-5.463Z';
const PIN_PATH_OFFSET = [263.268, 5.786] as const;
/** The white disc: centre and radius; the art's placement is relative to its box. */
const DISC = { cx: 12, cy: 12, r: 11 };

/** On-screen width in CSS px; the height follows the designers' proportions. */
const PIN_WIDTH = 34;
const PIN_WIDTH_SELECTED = 42;
/** $color-brand-deep — the designers' #0e3a2e is this colour to the eye. */
const PIN_BODY = '#0c3a2d';
/** $color-orange: the tapped pin, the same accent the old selected ring used. */
const PIN_BODY_SELECTED = '#b4531a';
const PIN_DISC = '#ffffff';
/** The emoji's size inside the disc, matched to how much of it the art fills. */
const EMOJI_SCALE = 0.62;

let pinPath: Path2D | undefined;

/**
 * Art is fetched once per file. Until it has loaded the pin shows the emoji, so
 * a pin is never blank; the map repaints when it arrives (onPinArtLoaded).
 */
const artImages = new Map<string, HTMLImageElement>();
const artListeners = new Set<() => void>();

export function onPinArtLoaded(listener: () => void): () => void {
  artListeners.add(listener);
  return () => artListeners.delete(listener);
}

function loadedArt(url: string): HTMLImageElement | null {
  let img = artImages.get(url);
  if (!img) {
    img = new Image();
    img.decoding = 'async';
    img.onload = () => artListeners.forEach((listener) => listener());
    img.src = url;
    artImages.set(url, img);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}

function drawPin(icon: ProduceIconSource, selected: boolean, art: HTMLImageElement | null) {
  const width = selected ? PIN_WIDTH_SELECTED : PIN_WIDTH;
  const height = (width * PIN_UNITS.height) / PIN_UNITS.width;
  // Drawn at the screen's pixel density, then handed to OL at CSS size, so the
  // art stays sharp on a phone instead of being a 34px bitmap scaled up.
  const ratio = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);

  const ctx = canvas.getContext('2d');
  if (!ctx) return { canvas, width, height };
  ctx.scale(canvas.width / PIN_UNITS.width, canvas.height / PIN_UNITS.height);

  pinPath ??= new Path2D(PIN_PATH_D);
  ctx.save();
  ctx.translate(PIN_PATH_OFFSET[0], PIN_PATH_OFFSET[1]);
  ctx.fillStyle = selected ? PIN_BODY_SELECTED : PIN_BODY;
  ctx.fill(pinPath);
  ctx.restore();

  ctx.beginPath();
  ctx.arc(DISC.cx, DISC.cy, DISC.r, 0, Math.PI * 2);
  ctx.fillStyle = PIN_DISC;
  ctx.fill();

  const box = DISC.r * 2;
  const left = DISC.cx - DISC.r;
  const top = DISC.cy - DISC.r;
  if (icon.art && art) {
    ctx.drawImage(
      art,
      left + icon.art.x * box,
      top + icon.art.y * box,
      icon.art.w * box,
      icon.art.h * box
    );
  } else {
    ctx.font = `${box * EMOJI_SCALE}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Emoji sit a touch high on their em box; nudge to the disc's optical centre.
    ctx.fillText(icon.emoji, DISC.cx, DISC.cy + box * 0.04);
  }

  return { canvas, width, height };
}

/** One canvas per look, not per feature: a map of apples draws one apple. */
const pinCache = new Map<string, Style>();

export function pinStyle(feature: FeatureLike): Style | Style[] {
  const count = feature.get('count') as number | undefined;
  if (count) return clusterStyle(count);

  const selected = feature.get('selected') === true;
  const icon = feature.get('icon') as ProduceIconSource;
  const art = icon.art ? loadedArt(icon.art.url) : null;

  const key = [
    icon.art && art ? icon.art.url : icon.emoji,
    selected ? 's' : '',
    window.devicePixelRatio || 1,
  ].join('|');
  let style = pinCache.get(key);
  if (!style) {
    const { canvas, width, height } = drawPin(icon, selected, art);
    style = new Style({
      image: new Icon({
        img: canvas,
        width,
        height,
        // The tip marks the place, not the centre of the disc.
        anchor: [0.5, 1],
      }),
      // A tapped pin is drawn over its neighbours rather than under them.
      zIndex: selected ? 1 : 0,
    });
    pinCache.set(key, style);
  }
  return style;
}

/**
 * Cluster symbology — a solid green disc with a white count. A single listing is
 * a teardrop and a group is a round disc, so the two cannot be confused, while
 * the green keeps them one family.
 *
 * Sizes are three discrete classes rather than a curve. Continuous scaling makes
 * neighbouring counts differ by a pixel or two, which nobody perceives, and it
 * outgrows the label: the count has to keep ~45% of the diameter to read as the
 * content of the symbol rather than something adrift inside it. The halo is what
 * carries "there are many here", so magnitude is not paid for in radius.
 */
const CLUSTER_FILL = '#12664b';
const CLUSTER_OUTLINE = '#f5f5f5';
/** The fill again, faded — a group reads as a disc with weight around it. */
const CLUSTER_HALO = 'rgba(18, 102, 75, 0.22)';

const CLUSTER_CLASSES = [
  { upTo: 10, radius: 17, font: 16 },
  { upTo: 100, radius: 19, font: 17 },
  { upTo: Infinity, radius: 21, font: 18 },
];

/** Turkish "bin" — 1.2b reads as 1200 without widening the disc. */
const clusterLabel = (count: number): string =>
  count >= 1000 ? `${Math.round(count / 100) / 10}b` : String(count);

function clusterStyle(count: number): Style[] {
  const klass = CLUSTER_CLASSES.find((c) => count < c.upTo) ?? CLUSTER_CLASSES[2];

  return [
    new Style({
      image: new CircleStyle({
        radius: klass.radius + 7,
        fill: new Fill({ color: CLUSTER_HALO }),
      }),
    }),
    new Style({
      image: new CircleStyle({
        radius: klass.radius,
        fill: new Fill({ color: CLUSTER_FILL }),
        stroke: new Stroke({ color: CLUSTER_OUTLINE, width: 2 }),
      }),
      text: new Text({
        text: clusterLabel(count),
        font: `700 ${klass.font}px sans-serif`,
        fill: new Fill({ color: '#ffffff' }),
      }),
    }),
  ];
}
